import {
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Stack,
  Textarea,
} from "@chakra-ui/react";
import CDS from "design-system";
import CaminoTrash from "components/Icon/CaminoTrash";
import { MdDragIndicator } from "react-icons/md";

import { FIELD_TYPE_ICON, FIELD_TYPE_LABEL, SELECT_FIELD_TYPES } from "../../constants";
import { slugify } from "../../helpers";
import type { CustomFormFieldPayload } from "../../models/payloads";
import type { EditorField } from "../../hooks/useCustomFormEditorState";

interface Props {
  field: EditorField | null;
  isOpen: boolean;
  onClose: () => void;
  onFieldChange: (patch: Partial<CustomFormFieldPayload>) => void;
}

export default function FieldInspector({ field, isOpen, onClose, onFieldChange }: Props) {
  const displayLabel = field?.label?.trim() || "Untitled field";
  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="sm" trapFocus={false}>
      <DrawerOverlay />
      <DrawerContent bg="bg.primary">
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor="border.subtle" pb={3}>
          {field ? (
            <Stack spacing={1}>
              <HStack spacing={2} color="content.primary">
                <Icon as={FIELD_TYPE_ICON[field.fieldType]} boxSize={4} color="content.tertiary" />
                <CDS.Text14 fontWeight="bold">{FIELD_TYPE_LABEL[field.fieldType]}</CDS.Text14>
              </HStack>
              <CDS.Text12 color="content.tertiary" fontWeight="normal">
                Editing: {displayLabel}
              </CDS.Text12>
            </Stack>
          ) : (
            <CDS.Text14 fontWeight="bold">Field</CDS.Text14>
          )}
        </DrawerHeader>
        <DrawerBody>
          {field ? <FieldSettings field={field} onFieldChange={onFieldChange} /> : null}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function FieldSettings({
  field,
  onFieldChange,
}: {
  field: EditorField;
  onFieldChange: (patch: Partial<CustomFormFieldPayload>) => void;
}) {
  const isSelect = SELECT_FIELD_TYPES.includes(field.fieldType);
  const choices = field.options.choices ?? [];

  const setChoices = (next: { value: string; label: string }[]) => {
    onFieldChange({ options: { ...field.options, choices: next } });
  };

  return (
    <Stack spacing={4} pt={4} pb={6}>
      <FormControl>
        <FormLabel textStyle="subtitleS" color="content.primary" mb={1}>
          Question
        </FormLabel>
        <Input
          size="sm"
          borderRadius="base"
          value={field.label}
          onChange={(e) => onFieldChange({ label: e.target.value })}
        />
      </FormControl>

      <Checkbox
        isChecked={field.required}
        onChange={(e) => onFieldChange({ required: e.target.checked })}
      >
        <CDS.Text14 color="content.primary">Required</CDS.Text14>
      </Checkbox>

      <FormControl>
        <FormLabel textStyle="subtitleS" color="content.primary" mb={1}>
          Help text
        </FormLabel>
        <Textarea
          size="sm"
          rows={2}
          borderRadius="base"
          value={field.helpText ?? ""}
          onChange={(e) => onFieldChange({ helpText: e.target.value || null })}
          placeholder="Extra guidance shown under the label"
        />
      </FormControl>

      {isSelect && (
        <FormControl>
          <FormLabel textStyle="subtitleS" color="content.primary" mb={1}>
            Options
          </FormLabel>
          <Stack spacing={2}>
            {choices.map((c, i) => (
              <HStack key={i} spacing={1} role="group">
                <Icon as={MdDragIndicator} color="content.tertiary" boxSize={4} opacity={0.5} />
                <Input
                  size="sm"
                  borderRadius="base"
                  value={c.label}
                  placeholder="Option label"
                  onChange={(e) => {
                    const next = [...choices];
                    next[i] = { ...c, label: e.target.value, value: c.value || slugify(e.target.value) };
                    setChoices(next);
                  }}
                />
                <CDS.IconButton
                  aria-label="Remove option"
                  customIcon={<CaminoTrash color="content.tertiary" boxSize={3.5} />}
                  size="sm"
                  variant="ghost"
                  onClick={() => setChoices(choices.filter((_, idx) => idx !== i))}
                />
              </HStack>
            ))}
            <CDS.Button
              intent={CDS.Button.intent.secondary}
              variant={CDS.Button.variant.ghost}
              size="sm"
              onClick={() => setChoices([...choices, { value: "", label: "" }])}
            >
              + Add option
            </CDS.Button>
          </Stack>
        </FormControl>
      )}

      <FormControl>
        <FormLabel textStyle="subtitleS" color="content.primary" mb={1}>
          Field key
        </FormLabel>
        <Input
          size="sm"
          borderRadius="base"
          isReadOnly
          value={fieldKeyFor(field)}
          bg="bg.tertiary"
        />
        <CDS.Text12 color="content.tertiary" mt={1}>
          Used by integrations. Not editable yet.
        </CDS.Text12>
      </FormControl>
    </Stack>
  );
}

// Answers, CSV column tokens, and the AI tools all key fields by uuid — never the DB id.
function fieldKeyFor(field: EditorField): string {
  return field.uuid ?? "(assigned after save)";
}
